import { Permission } from '@bookora/shared';

import { sectionFeature } from '@/features/plan/section-features';

import { tourFeatureOn } from '../resolve-steps';
import type { TourDef } from './types';

/**
 * «ثبت هزینه» is the «حسابداری و سود و زیان» plan feature (owner D6). On a
 * plan without it the header still shows the button — blocked, with the plan
 * that has it — so, once plan access has said so, the actions step speaks of
 * payments only. No step spotlights a plan-locked tab: the statement, the
 * payments and the period are open on every plan (owner round 4, C5).
 */
const EXPENSES = sectionFeature('accounting:expenses');

export const ACCOUNTING_TOUR: TourDef = {
  key: 'accounting',
  version: 1,
  surface: 'page',
  title: 'حسابداری',
  hubDescription: 'تب‌های گزارش، بازهٔ زمانی و ثبت دریافت و هزینه.',
  offer: {
    body: 'در چند قدم کوتاه نشونتون می‌دیم گزارش‌ها، بازهٔ زمانی و ثبت دریافت و هزینه کجاست.',
  },
  route: { path: '/accounting', match: (pathname) => pathname === '/accounting' },
  requires: {
    any: [
      Permission.FINANCE_VIEW_REVENUE,
      Permission.FINANCE_VIEW_COSTS,
      Permission.FINANCE_VIEW_PROFIT,
    ],
  },
  autoOffer: true,
  maxSteps: 3,
  minSteps: 2,
  steps: [
    {
      id: 'tabs',
      anchor: 'accounting.tabs',
      placement: 'bottom',
      title: 'گزارش‌های مالی',
      body: 'سود و زیان، دریافت‌ها، هزینه‌ها و سهم پزشکان هرکدوم یک تب جدا دارن. فقط تب‌هایی رو می‌بینید که به اون‌ها دسترسی دارید.',
    },
    {
      id: 'period',
      anchor: 'accounting.period',
      placement: 'bottom',
      title: 'بازهٔ گزارش',
      body: 'ماه جاری، ماه قبل یا هر بازهٔ دلخواه؛ همهٔ مبلغ‌های این تب بر اساس بازهٔ انتخاب‌شده نمایش داده می‌شن.',
    },
    {
      // `accounting.actions` wraps ONLY the action buttons and is rendered
      // only when one of them is drawn (the tour chip is its sibling, never
      // inside it), so the anchor's presence is the gate. No `requires`: the
      // expense button is also drawn, explained and blocked, for a custom
      // role without the key. The body names only what this viewer records:
      // a payment needs package.sell, an expense finance.manage_expenses.
      id: 'actions',
      anchor: 'accounting.actions',
      placement: 'bottom-end',
      title: 'ثبت دریافت و هزینه',
      body: (ctx) => {
        const payments = ctx.can(Permission.FINANCE_RECORD_PAYMENT);
        const expenses =
          ctx.can(Permission.FINANCE_MANAGE_EXPENSES) && tourFeatureOn(EXPENSES) !== false;
        if (payments && expenses) {
          return 'دریافت از بیمار و هزینه‌های کلینیک رو اینجا ثبت کنید؛ بوکورا پولی جابه‌جا نمی‌کنه، فقط ثبت می‌کنه. هزینهٔ خرید کالا هنگام ورود کالا به انبار ثبت می‌شه.';
        }
        if (payments) {
          return 'دریافت از بیمار رو اینجا ثبت کنید؛ بوکورا پولی جابه‌جا نمی‌کنه، فقط ثبت می‌کنه.';
        }
        if (expenses) {
          return 'هزینه‌های کلینیک، مثل اجاره و قبض‌ها، رو اینجا ثبت کنید. هزینهٔ خرید کالا هنگام ورود کالا به انبار ثبت می‌شه.';
        }
        // The expense key without the plan feature: the button is drawn
        // blocked, and says which plan has it.
        return 'ثبت هزینه در پلن فعلی کلینیک در دسترس نیست. پلن موردنیاز رو کنار دکمه می‌بینید.';
      },
    },
  ],
};
